import React, { useCallback, useEffect, useState } from "react";
import { CloseOutlined } from "@ant-design/icons";
import { Button, Drawer } from "antd";
import SprServisMain from "./spr-servis-main";
import { VID_DIARY } from "./spr-servis-utils";
import "./spr-servis.css";

const DRAWER_WIDTH_KEY = "planOnline.sprServis.cardDrawer.width";
const MIN_WIDTH = 420;
const MAX_WIDTH = 720;
const DEFAULT_WIDTH = 480;
const SCREEN_MARGIN = 100;

const maxAllowedWidth = () => {
  if (typeof window === "undefined") return MAX_WIDTH;
  return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, window.innerWidth - SCREEN_MARGIN));
};

const clampWidth = (value) => {
  const max = maxAllowedWidth();
  const n = Number(value);
  if (!Number.isFinite(n)) return Math.min(DEFAULT_WIDTH, max);
  return Math.min(max, Math.max(MIN_WIDTH, Math.round(n)));
};

const loadWidth = () => {
  try {
    return clampWidth(localStorage.getItem(DRAWER_WIDTH_KEY) ?? DEFAULT_WIDTH);
  } catch {
    return DEFAULT_WIDTH;
  }
};

const saveWidth = (value) => {
  try {
    localStorage.setItem(DRAWER_WIDTH_KEY, String(clampWidth(value)));
  } catch {
    /* ignore */
  }
};

/**
 * Drawer карточки вида диагноза / записи в дневник.
 */
const SprServisDrawer = ({ open, vid, id, isNew = false, canEdit, onClose, onSaved }) => {
  const [size, setSize] = useState(loadWidth);
  const isDiary = vid === VID_DIARY;
  const title = isNew
    ? isDiary
      ? "Новый вид записи в дневник"
      : "Новый вид диагноза"
    : isDiary
      ? "Вид записи в дневник"
      : "Вид диагноза";

  useEffect(() => {
    if (!open) return undefined;
    const onWinResize = () => setSize((prev) => clampWidth(prev));
    onWinResize();
    window.addEventListener("resize", onWinResize);
    return () => window.removeEventListener("resize", onWinResize);
  }, [open]);

  const onResize = useCallback((next) => {
    setSize(clampWidth(next));
  }, []);

  const onResizeEnd = useCallback(
    (finalSize) => {
      const next = clampWidth(typeof finalSize === "number" ? finalSize : size);
      setSize(next);
      saveWidth(next);
    },
    [size]
  );

  return (
    <Drawer
      placement="right"
      title={title}
      size={size}
      maxSize={maxAllowedWidth()}
      open={open}
      onClose={onClose}
      destroyOnHidden
      closable={false}
      rootClassName="spr-servis-drawer"
      extra={
        <Button
          type="text"
          className="spr-servis-drawer__close"
          icon={<CloseOutlined />}
          aria-label="Закрыть"
          title="Закрыть"
          onClick={onClose}
        />
      }
      styles={{
        body: { display: "flex", flexDirection: "column", paddingTop: 12 }
      }}
      resizable={{
        onResize,
        onResizeEnd
      }}
    >
      {open && vid != null && (id != null || isNew) ? (
        <SprServisMain
          vid={vid}
          id={id}
          isNew={isNew}
          open={open}
          canEdit={canEdit}
          onSaved={onSaved}
          onCancel={onClose}
        />
      ) : null}
    </Drawer>
  );
};

export default SprServisDrawer;
